import React, { useEffect, useMemo, useState } from "react";
import { mapValues, merge } from "lodash-es";

import { FormBuilder } from "shared/components";
import { IAPIState, IFormButtons } from "shared/types";
import { convertDimensions, useUnitType } from "shared/utils";
import { useVenuePageContext } from "Venues/contexts";
import { IEquipment, IEquipmentSubcategoryItem } from "Venues/types";
import { getFormData, defaultValues } from "./formData";
import { Distance, DistanceUnits, Weight, WeightUnits } from "shared/utils";
import { getEquipmentMainCategory } from "Venues/utils";

interface EquipmentFormProps {
  apiStatus?: IAPIState;
  hasError?: boolean;
  defaultEquipmentValues?: IEquipment;
  disableStages?: string[]; // stage IDs
  hideCategories?: boolean;
  buttons?: IFormButtons;
  resetForm?: boolean;
  handleSubmit: (equipment: IEquipment) => void;
}

export const EquipmentForm: React.FC<EquipmentFormProps> = ({
  defaultEquipmentValues,
  disableStages,
  hideCategories,
  hasError,
  apiStatus,
  resetForm,
  handleSubmit,
  ...rest
}) => {
  const [subCategories, setSubCategories] = useState<IEquipmentSubcategoryItem[]>();

  const { unitType } = useUnitType();
  const { venueStages } = useVenuePageContext();

  useEffect(() => {
    if (resetForm) {
      setSubCategories(undefined);
    }
  }, [resetForm]);

  const mergedDefaultValues = useMemo(() => {
    if (defaultEquipmentValues) {
      const { dimensions, weight, category, stages, ...rest } = defaultEquipmentValues;

      const mainCategory = getEquipmentMainCategory(category);
      if (mainCategory?.categories) {
        setSubCategories(mainCategory.categories);
      }

      return merge(
        {},
        defaultValues,
        rest,
        category && {
          mainCategory: mainCategory?.category,
          category,
        },
        weight && {
          weight: new Weight(weight, WeightUnits.g),
        },
        dimensions && {
          dimensions: mapValues(dimensions, val => new Distance(val, DistanceUnits.mm)),
        },
        stages && {
          stages: stages.map(({ id }) => id),
        }
      );
    }
    return defaultValues;
  }, [defaultEquipmentValues]);

  const onSubmit = (data: any) => {
    const { mainCategory, ...rest } = data;
    const cleanData: IEquipment = convertDimensions(rest);

    handleSubmit(cleanData);
  };

  const fields = useMemo(
    () => getFormData(venueStages, subCategories, setSubCategories, hideCategories, disableStages),
    [venueStages, setSubCategories, subCategories, hideCategories, disableStages]
  );

  return (
    <FormBuilder
      unitType={unitType}
      defaultValues={mergedDefaultValues}
      fields={fields}
      onSubmit={onSubmit}
      isSubmitting={apiStatus === IAPIState.pending}
      style={{
        gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
      }}
      resetForm={resetForm}
      apiErrorMessage={hasError}
      {...rest}
    />
  );
};
